<script>
	import { tick } from "svelte";
	import { page } from "$app/stores";
	import { slugify, safeStringify } from "cando-util";

	import SvelteFa from "$lib/components/SvelteFa.svelte";

	import { detectFieldType, detectFieldAction } from "./tableUtils.js";
	import { store } from "$lib/utilities/store";

	import AuthorizedExtensions from "../Roles/AuthorizedExtensions.svelte";
	import AuthorizedAccounts from "../Roles/AuthorizedAccounts.svelte";
	import ReadMore from "$lib/components/ReadMore.svelte";
	import RenderJson from "$lib/components/RenderJSON/RenderJSON.svelte";
	import EmbeddedCalls from "../Management/Cases/EmbeddedCalls.svelte";
	import TextInput from "$lib/components/TextInput.svelte";
	import Textarea from "$lib/components/Textarea.svelte";
	import PhoneNumber from "$lib/components/PhoneNumber.svelte";
	import Currency from "$lib/components/Currency.svelte";
	import Select from "$lib/components/Select.svelte";
	import Events from "../Management/Submissions/Events.svelte";
	import FileUploads from "$lib/components/FormSubmission/FileUploads.svelte";
	import Autocomplete from "$lib/components/Autocomplete.svelte";
	import Tasks from "../Management/Submissions/Tasks.svelte";
	import DeleteModal from "$lib/components/DeleteModal.svelte";
	import Suggestion from "../Suggestion.svelte";
	import MarkedContent from "$lib/components/MarkedContent.svelte";
	import SettingsFormInput from "../SettingsFormInput.svelte";
	import Timer from "$lib/components/Timer.svelte";
	import CallIntakeForm from "./CallIntakeForm.svelte";
	import CallTranscription from "./CallTranscription.svelte";

	export let fld = {};
	export let col;
	export let docId;
	export let docs;
	export let action;
	export let asBlock = false;
	export let allowEdit = true;
	export let editMode = false;

	let lang;
	$: pageLang = $page?.data?.lang || "";
	$: lang =
		pageLang && window.location.pathname.includes(`/${pageLang}/`)
			? pageLang
			: "";

	const containerTag = asBlock ? "div" : "td";

	const getFieldTypeForDoc = (fld, docId) => {
		if (fld.type) return fld.type;
		const record = { __docId: docId, ...docs?.[docId] };
		if (typeof fld.dynamicTypeResolver === "function") {
			return fld.dynamicTypeResolver(record) || undefined;
		}
		if (fld.key) {
			return detectFieldType(fld.key, record?.[fld.key]);
		}
		return undefined;
	};

	const getActionForDoc = (fld, docId) => {
		if (fld.action) return fld.action;
		const record = { __docId: docId, ...docs?.[docId] };
		if (typeof fld.dynamicActionResolver === "function") {
			return fld.dynamicActionResolver(docId, record) || undefined;
		}
		if (fld.key) {
			return detectFieldAction(fld.key, record?.[fld.key], col, docId);
		}
		return undefined;
	};

	const normalizeFileEntries = (entries) => {
		if (!entries) return {};
		if (Array.isArray(entries)) {
			return entries.reduce((acc, file, index) => {
				if (!file) return acc;
				const key = file?.name || `file-${index}`;
				acc[key] = file;
				return acc;
			}, {});
		}
		if (typeof entries === "object") return entries;
		return {};
	};

	const isIdentifierField = (fld) =>
		Boolean(fld?.isIdentifier || fld?.key === "__docId");

	const resolveFieldOption = (option, docId) =>
		typeof option === "function" ? option(docId) : option;

	const READ_MORE_TOTAL_LIMIT = 200;
	const READ_MORE_LINE_LIMIT = 80;

	const shouldUseReadMore = (value) => {
		if (typeof value !== "string") return false;
		if (value.includes("<a href=")) return false;
		const trimmed = value.trim();
		if (!trimmed) return false;
		if (trimmed.length > READ_MORE_TOTAL_LIMIT) return true;
		return trimmed
			.split(/\r?\n/)
			.some((line) => line.trim().length > READ_MORE_LINE_LIMIT);
	};

	const loadValue = async (docId, fn) => {
		if (fn === "function") cellValue = await fld.function(docId);
		if (fn === "value") fldValue = await fld.value(docId);
	};

	let cellValue;
	$: if (typeof fld.function === "function" && docId) {
		loadValue(docId, "function");
	}

	const keepScrolledToBottom = (node, initialBehavior = "smooth") => {
		let behavior = initialBehavior;
		const scrollToLatest = async () => {
			await tick();
			const scrollTarget =
				node.querySelector("[data-auto-scroll-container]") ?? node;
			if (typeof scrollTarget.scrollTo === "function") {
				scrollTarget.scrollTo({
					top: scrollTarget.scrollHeight,
					behavior,
				});
			} else {
				scrollTarget.scrollTop = scrollTarget.scrollHeight;
			}
		};
		const observer = new MutationObserver((mutations) => {
			if (mutations.some((mutation) => mutation.addedNodes.length)) {
				scrollToLatest();
			}
		});

		observer.observe(node, { childList: true, subtree: true });
		scrollToLatest();

		return {
			update(nextBehavior) {
				behavior = nextBehavior || "smooth";
			},
			destroy() {
				observer.disconnect();
			},
		};
	};
	let fldValue;
	$: if (typeof fld.value === "function" && docId) {
		loadValue(docId, "value");
	}

	$: isIdColumn = isIdentifierField(fld) && !!action;
	$: useReadMore =
		!isIdColumn &&
		shouldUseReadMore(cellValue) &&
		fld.useReadMore !== false;

	$: key = typeof fld.key === "function" ? fld.key(docId) : fld.key;
	// make sure fld has an action.  Tasks need to check a few keys though so they're excluded
	$: canEdit =
		typeof getActionForDoc(fld, docId) === "function" && allowEdit
			? true
			: false;
</script>

{#if fld.hide && fld.hide?.(docId)}
	<svelte:element this={containerTag}>&nbsp;</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "SettingFormInput"}
	<svelte:element this={containerTag} class="text-wrap flex">
		{#if $store?.[col]?.docs?.[docId]?.[fld.key]}
			<SettingsFormInput
				{col}
				settingId={docId}
				fld={{
					key: fld.key,
					...$store?.[col]?.docs?.[docId]?.[fld.key],
				}}
				{lang}
				handleChange={getActionForDoc(fld, docId)}
				showKey={false}
				{editMode}
			/>
		{/if}
	</svelte:element>
{:else if ["Image", "file"].includes(getFieldTypeForDoc(fld, docId))}
	<svelte:element this={containerTag}>
		<FileUploads
			value={fld.function(docId)}
			label={key}
			readonly={fld.readonly || !getActionForDoc(fld, docId)}
			accept={fld.accept}
			action={getActionForDoc(fld, docId)}
			{docId}
			{col}
			{key}
			existingDoc={docs[docId]}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "TextObject"}
	<svelte:element this={containerTag} class="align-top">
		<div class="max-h-18 overflow-auto">
			<RenderJson
				strJson={safeStringify(
					docs?.[docId]?.[key]?.value ?? docs?.[docId]?.[key],
				)}
				componentId={`${slugify(fld.heading)}-${slugify(docId)}-textobject`}
				widthClass="w-full max-w-[32rem] overflow-auto"
			/>
		</div>
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "RenderJson"}
	{@const jsonValue = fld.function(docId)}
	{@const hasJsonValue = !(
		jsonValue === null || typeof jsonValue === "undefined"
	)}
	<svelte:element this={containerTag} class="align-top">
		{#if hasJsonValue}
			<RenderJson
				strJson={typeof jsonValue === "string"
					? jsonValue
					: safeStringify(jsonValue)}
				componentId={fld.renderJsonOptions?.componentId ??
					`${slugify(fld.heading)}-${slugify(docId)}-json`}
				divIdPrefix={fld.renderJsonOptions?.divIdPrefix ?? "tablejson"}
				widthClass={fld.renderJsonOptions?.widthClass ??
					"overflow-auto max-w-[40rem]"}
				spacingClass={fld.renderJsonOptions?.spacingClass ?? "p-0"}
				borderClass={fld.renderJsonOptions?.borderClass ??
					"border border-primary/50"}
				otherClass={fld.renderJsonOptions?.otherClass ??
					"rounded bg-base-200 text-xs"}
				isExpanded={fld.renderJsonOptions?.isExpanded ?? false}
			/>
		{:else}
			&nbsp;
		{/if}
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "checkbox"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<input
			type="checkbox"
			id={`${slugify(`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)} input`)}`}
			checked={fldValue}
			{docId}
			itemId={docId}
			value={key}
			class="checkbox checkbox-primary"
			{key}
			on:change={getActionForDoc(fld, docId)}
		/>
		<span class={`label-text ml-2 text-left`}>{fld.heading}</span>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "date"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<input
			type="date"
			id={`${slugify(`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)} input`)}`}
			{docId}
			itemId={docId}
			{key}
			class={`w-full input input-bordered text-lg sm:text-xl`}
			name={fld.heading}
			value={fldValue}
			on:change={getActionForDoc(fld, docId)}
			readonly={fld.readonly}
			disabled={fld.disabled && fld.disabled(docId)}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "datetime"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<input
			type="datetime-local"
			id={`${slugify(`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)} input`)}`}
			{docId}
			itemId={docId}
			{key}
			class={`w-full input input-bordered text-lg sm:text-xl`}
			name={fld.heading}
			value={fldValue}
			on:change={getActionForDoc(fld, docId)}
			readonly={fld.readonly}
			disabled={fld.disabled && fld.disabled(docId)}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && ["dropdown", "select"].includes(getFieldTypeForDoc(fld, docId))}
	<svelte:element this={containerTag} class="cursor-pointer">
		<Select
			value={fldValue}
			name={`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)} input`}
			{key}
			options={typeof fld.options === "function"
				? fld.options(docId)
				: fld.options}
			handleChange={getActionForDoc(fld, docId)}
			readonly={fld.readonly}
			disabled={fld.disabled && fld.disabled(docId)}
			itemId={docId}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && ["autocomplete"].includes(getFieldTypeForDoc(fld, docId))}
	<svelte:element this={containerTag}>
		<div>
			<Autocomplete
				value={fldValue}
				options={typeof fld.options === "function"
					? fld.options(docId)
					: fld.options}
				handleChange={getActionForDoc(fld, docId)}
				name={`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)} input`}
				{key}
				itemId={docId}
				disabled={fld.disabled && fld.disabled(docId)}
			/>
		</div>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "Tasks"}
	<svelte:element this={containerTag}>
		<Tasks
			tasks={fldValue}
			{col}
			{docId}
			{key}
			moveAction={allowEdit ? fld.moveAction : false}
			selectAction={allowEdit ? fld.selectAction : false}
			existingDoc={docs[docId]}
		/>
	</svelte:element>
{:else if canEdit && ["text", "email"].includes(getFieldTypeForDoc(fld, docId))}
	<svelte:element this={containerTag} class="cursor-pointer">
		<TextInput
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)}`}
			{key}
			handleInputChange={getActionForDoc(fld, docId)}
			itemId={docId}
			cls={"input input-bordered input-primary w-full"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && ["textarea"].includes(getFieldTypeForDoc(fld, docId))}
	<svelte:element this={containerTag} class="cursor-pointer">
		<Textarea
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)}`}
			{key}
			handleInputChange={getActionForDoc(fld, docId)}
			itemId={docId}
			additionalClass={"input input-bordered input-primary text-wrap"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "phone"}
	<svelte:element this={containerTag} class="block cursor-pointer">
		<PhoneNumber
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)}`}
			{key}
			handleChange={getActionForDoc(fld, docId)}
			itemId={docId}
			cls={"input input-bordered input-primary"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "currency"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<Currency
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${getFieldTypeForDoc(fld, docId)} input`}
			{key}
			handleInputChange={getActionForDoc(fld, docId)}
			itemId={docId}
			cls={"input input-bordered input-primary w-full"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "button" && !!fld.label(docId)}
	<svelte:element this={containerTag}>
		<button
			class={`${fld.class || "btn btn-primary btn-sm"}`}
			disabled={fld.disabled && fld.disabled(docId)}
			on:click={getActionForDoc(fld, docId)}
			{docId}
			itemId={docId}
		>
			{#if fld.icon}
				<SvelteFa icon={fld.icon} size="1x" />
			{/if}
			{#if fld.label}
				{fld.label(docId)}
			{/if}
		</button>
	</svelte:element>
{:else if canEdit && getFieldTypeForDoc(fld, docId) === "DeleteModal"}
	<DeleteModal
		colDtls={{ name: fld.name, singular: fld.singular }}
		obj={fldValue}
		deleteFunction={getActionForDoc(fld, docId)}
		closeModalOnDelete={fld.closeModalOnDelete}
		btnCls={`${fld.btnCls || "w-full btn btn-warning rounded-btn modal-button"}`}
		disabled={fld.disabled && fld.disabled(docId)}
		redirectLink={fld.redirectLink}
	/>
{:else if getFieldTypeForDoc(fld, docId) === "AuthorizedExtensions"}
	<svelte:element this={containerTag}>
		<AuthorizedExtensions email={docId} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "AuthorizedAccounts"}
	<svelte:element this={containerTag}>
		<AuthorizedAccounts email={docId} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "ReadMore"}
	<svelte:element this={containerTag} class="text-wrap">
		<ReadMore content={fld.function(docId)} previewHeightClass="max-h-18" />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "EmbeddedCalls"}
	<svelte:element this={containerTag} class="text-wrap">
		<EmbeddedCalls calls={fld.function(docId)} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "Events"}
	<svelte:element this={containerTag} class="text-wrap">
		<Events
			events={fld.function(docId)}
			{col}
			{docId}
			{key}
			action={getActionForDoc(fld, docId)}
			existingDoc={docs[docId]}
		/>
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "MarkedContent"}
	<svelte:element this={containerTag} class="text-wrap">
		<MarkedContent markedContent={fldValue} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "Timer"}
	<svelte:element this={containerTag}>
		{#if resolveFieldOption(fld.start, docId)}
			<Timer
				start={resolveFieldOption(fld.start, docId)}
				end={resolveFieldOption(fld.end, docId)}
				format={resolveFieldOption(fld.format, docId)}
				cls={resolveFieldOption(fld.cls, docId)}
			/>
		{:else}
			&nbsp;
		{/if}
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "CallIntakeForm"}
	<svelte:element this={containerTag} class="align-top">
		<CallIntakeForm record={docs?.[docId]} {col} {lang} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "CallTranscription"}
	<svelte:element
		this={containerTag}
		class={`align-top ${asBlock ? "h-full" : ""}`}
	>
		<CallTranscription {docId} fill={asBlock} />
	</svelte:element>
{:else if fld.autoScrollToBottom}
	<svelte:element this={containerTag} class="text-wrap align-top">
		<div use:keepScrolledToBottom={fld.autoScrollBehavior || "smooth"}>
			{@html cellValue}
		</div>
	</svelte:element>
{:else}
	<svelte:element
		this={containerTag}
		class={`${useReadMore ? "text-wrap align-top" : "text-nowrap align-top"}`}
	>
		{#if isIdColumn}
			<button
				class="link underline"
				type="button"
				on:click|stopPropagation={() => action(docId)}
			>
				{@html cellValue}
			</button>
		{:else if useReadMore}
			<ReadMore content={cellValue} previewHeightClass="max-h-18" />
		{:else}
			{@html cellValue}
		{/if}
	</svelte:element>
{/if}
